import { useEffect, useState } from 'react';
import { Col, Row, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getImageAnalysis2 } from '../../utils/imageAnyliser';
import { ExpandOutIcon, DetailsIcon, MicrophoneIcon, DeviceIcon } from '../Icons';
import VolumeControls from '../Layout/components/PlayingBar/Volume';
import PlayControls from '../Layout/components/PlayingBar/PlayControls';
import SongDetails from '../Layout/components/PlayingBar/SongDetails';
import { DetailsButton, LyricsButton, QueueButton, DeviceButton } from '../Layout/components/PlayingBar/ExtraButtons';

import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '../Tooltip';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { spotifyActions } from '../../store/slices/spotify';

interface FullScreenPlayerProps {
  onExit: () => Promise<void>;
}

const ExpandOutButton: FC<FullScreenPlayerProps> = (props) => {
  const [t] = useTranslation(['playingBar']);
  return (
    <Tooltip title={t('Exit full screen')}>
      <button 
        style={{ marginLeft: 5, marginRight: 5, cursor: 'pointer' }} 
        onClick={props.onExit}
        className='active-icon-button tablet-hidden'
      >
        <ExpandOutIcon />
      </button>
    </Tooltip>
  );
};

export const FullScreenPlayer: FC<FullScreenPlayerProps> = (props) => {
  const [bgColor, setBgColor] = useState<string>('#121212');
  const navigate = useNavigate();

  const handleArtistClick = (artistId: string) => {
    if (!artistId || artistId === '0') return;
    props.onExit().then(() => {
      navigate(`/artist/${artistId}`);
    });
  };

  const [scrollY, setScrollY] = useState(0);
  
  const song = useAppSelector(
    (state) => state.spotify.state?.track_window.current_track,
    (a, b) => a?.id === b?.id
  );
  
  const upcomingSongs = useAppSelector((state) => state.queue.queue);

  useEffect(() => {
    if (song?.album.images[0]?.url) {
      getImageAnalysis2(song.album.images[0].url)
        .then((color) => {
          setBgColor(color || '#121212');
        })
        .catch(() => setBgColor('#121212'));
    }
  }, [song?.album.images[0]?.url]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollY(e.currentTarget.scrollTop);
  };

  const heroOpacity = Math.max(0, 1 - scrollY / 300);
  const heroScale = Math.max(0.8, 1 - scrollY / 500);
  const heroTranslate = scrollY * 0.4;

  const fallbackImage = song?.album.images[0]?.url;

  return (
    <div 
      className='Full-screen-page'
      style={{ 
        backgroundColor: bgColor,
        backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, #000000 100%)`,
        transition: 'background-color 1s ease'
      }}
    >
      <div 
        className='fs-scrollable-content' 
        onScroll={handleScroll}
      >
        <div className='fs-top-bar'>
          <div className='fs-song-title'>{song?.name}</div>
          <div className='fs-top-actions'>
            <button className="top-action-btn"><MicrophoneIcon /></button>
            <button className="top-action-btn"><DeviceIcon active={false} /></button>
            <button className="top-action-btn" style={{ letterSpacing: 2, fontWeight: 'bold' }}>...</button>
            <button className="top-action-btn" onClick={props.onExit}><ExpandOutIcon /></button>
          </div>
        </div>

        <div className='fs-hero-wrapper'>
          <div 
            className='fs-hero' 
            style={{ 
              opacity: heroOpacity, 
              transform: `scale(${heroScale}) translateY(${heroTranslate}px)` 
            }}
          >
            {song && (
              <img 
                src={fallbackImage} 
                alt="Album Art" 
                className="fs-large-album" 
              />
            )}
          </div>
        </div>

        <div style={{ paddingBottom: '80px', alignSelf: 'stretch', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'linear-gradient(to bottom, transparent 0%, #000000 300px, #000000 100%)' }}>
          <div className='fs-sections'>
            <div className='fs-section fs-3-col'>
              <div className='fs-col fs-card'>
                <div className='col-header'>
                  <h3>About the artist</h3>
                </div>
                <img className='artist-avatar' src={(song?.artists[0] as any)?.image?.[0]?.url || (song?.artists[0] as any)?.image || fallbackImage} alt="Artist Avatar" />
                <div className='about-artist-info'>
                  <div className='artist-name-block'>
                    <h4 
                      className='clickable-artist'
                      onClick={() => handleArtistClick((song?.artists[0] as any)?.id)}
                    >{song?.artists[0]?.name || 'Artist Name'}</h4>
                    <p>11,422,161 monthly listeners</p>
                  </div>
                  <button className='follow-btn'>Follow</button>
                </div>
                <div className='artist-bio'>
                  {song?.artists[0]?.name || 'Artist'} is a film composer and independent artist creating music across major Hindi films.
                </div>
              </div>
              
              <div className='fs-col fs-card'>
                <div className='col-header'>
                  <h3>Credits</h3>
                  <span className='show-all'>Show all</span>
                </div>
                
                {song?.artists.map((artist, i) => (
                  <div key={i} className='credit-item'>
                    <div className='credit-info'>
                      <p 
                        className='clickable-artist'
                        onClick={() => handleArtistClick((artist as any).id)}
                      >{artist.name}</p>
                      <span>{i === 0 ? 'Main Artist • Composer' : 'Main Artist'}</span>
                    </div>
                    <button className='follow-btn'>Follow</button>
                  </div>
                ))}
              </div>
              
              <div className='fs-col fs-card'>
                <div className='col-header'>
                  <h3>Next in queue</h3>
                  <span className='show-all'>Open queue</span>
                </div>
                
                {upcomingSongs.slice(0, 3).map((upcomingSong: any, i: number) => (
                  <div key={i} className='queue-item'>
                    <div className='queue-left'>
                      <img src={upcomingSong?.album?.images[0]?.url || upcomingSong?.image || fallbackImage} alt="Art" />
                      <div className='queue-info'>
                        <p>{upcomingSong?.name}</p>
                        <span 
                          className='clickable-artist'
                          onClick={() => handleArtistClick(upcomingSong?.artists?.[0]?.id)}
                        >{upcomingSong?.artists?.[0]?.name || upcomingSong?.subtitle}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='fs-bottom-bar'>
        <div className='w-full p-4 flex items-center justify-between h-full mobile-hidden'>
          <div style={{ width: '30%', minWidth: '300px' }}>
            <SongDetails />
          </div>
          <div style={{ width: '40%', display: 'flex', justifyContent: 'center' }}>
            <PlayControls />
          </div>
          <div style={{ width: '30%', minWidth: '300px', display: 'flex', justifyContent: 'flex-end' }}>
            <Row gutter={18} align='middle'>
              <DetailsButton />
              <LyricsButton />
              <QueueButton />
              <Col className='hiddable-icon'>
                <DeviceButton />
              </Col>
              <Col>
                <VolumeControls />
              </Col>
              <Col>
                <ExpandOutButton onExit={props.onExit} />
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
};

FullScreenPlayer.displayName = 'FullScreenPlayer';
